import React from 'react';
import { X, Check, Megaphone, Rocket, Palette, Clock } from 'lucide-react';

interface FeatureAnnouncementProps {
  variant?: 'popup' | 'page';
  onDismiss?: () => void;
}

type Accent = 'blue' | 'emerald' | 'amber';
type ItemType = 'update' | 'upcoming' | 'notice';

interface AnnouncementItem {
  date: string;
  title: string;
  description: string;
  videoUrl?: string;
  features: string[];
  accent: Accent;
  type?: ItemType;
}

export const NEW_UPDATES_COUNT = 1;

// Most recent first
const announcements: AnnouncementItem[] = [
  {
    date: 'Jun 9, 2026',
    title: 'Watchlist, Scan Progress & Brand Risk Sprint',
    description:
      'The biggest quality-of-life update yet: save domains to a persistent watchlist, track scan progress in real time, and see brand risk front and center on every available result.',
    accent: 'blue',
    type: 'update',
    features: [
      'Domain Watchlist — save up to 50 domains, persisted across sessions with no account required',
      'Auto re-check: top 10 watched domains are refreshed automatically on every visit',
      'Change detection: price drops and availability changes highlighted with per-row indicators',
      'Live scan progress bar — shows X / Y TLDs scanned and available count as results stream in',
      'Post-scan summary with three message variants based on how many domains were found available',
      'Brand risk analysis now appears before pricing in every result card — the most important signal first',
      'Top Keywords rewritten as a full sortable table: 100 keywords, 7d / 30d / 90d / All period filters, inline search, and paginated results',
      'Namecheap pricing integrated — compare registration prices across Name.com and Namecheap side by side',
    ],
  },
  {
    date: 'Jun 7, 2026',
    title: 'Dashboard, Sidebar & Search Overhaul',
    description:
      'DomBase got a major UX upgrade: a collapsible SaaS-style sidebar replaces the top navbar, the home page is now a live intelligence dashboard, and the search experience is smarter than ever.',
    accent: 'blue',
    type: 'update',
    features: [
      'Collapsible sidebar navigation — icon-only or full labels, state saved across sessions',
      'Home dashboard with live widgets: top keywords, security news feed, expiring domains, and registrar health',
      'Quick-search widget on the home dashboard — keyword or domain, one click away',
      'Search page fills the viewport with a centered search box when no query is active',
      'Search input now syncs to URL params — share or bookmark a search and the input is pre-filled',
      'Radar split into two routes: Status (18 services monitored) and Security News',
      'Expanded service monitoring to 18 services across DNS, SSL, CDN, Hosting, Cloud and Email categories',
    ],
  },
  {
    date: 'May 26, 2026',
    title: 'Complete Visual Redesign — Flat Design System',
    description:
      'DomBase has been rebuilt from the ground up with a bold flat design system. Cleaner layouts, sharper typography, and a faster UI that gets out of your way so you can focus on finding the right domain.',
    accent: 'blue',
    type: 'update',
    features: [
      'Outfit geometric sans-serif — more legible and distinctive at all sizes',
      'Blue color-block hero with geometric decorations replaces the old gradient background',
      'Search is front and center — no distractions, no ads',
      'Feature cards, stats bar, and "How it works" section added to home page',
      'Flat gray-100 input with blue focus border replaces the busy old search form',
      'Flat announcement modal with per-entry color coding',
      'No shadows, no gradients — everything on the same plane',
    ],
  },
  {
    date: 'Coming soon',
    title: 'Upcoming: Price History Charts',
    description:
      'Track how registration and renewal prices have moved over time for any domain or TLD. Make smarter timing decisions when buying.',
    accent: 'blue',
    type: 'upcoming',
    features: [
      'Historical pricing data per domain and per TLD',
      'Registration, renewal, and transfer price trends',
      'Visible on the domain detail page',
      'Helps identify the best time to register or renew',
    ],
  },
  {
    date: 'Nov 14, 2025',
    title: 'GoDaddy Pricing Temporarily Unavailable',
    description:
      "Due to GoDaddy's new API access policies, we're temporarily unable to show GoDaddy pricing. Name.com pricing remains fully available.",
    accent: 'amber',
    type: 'notice',
    features: [
      'GoDaddy API now requires 10+ domains or Discount Domain Club membership',
      'Name.com pricing continues to work normally',
      'All other features remain fully functional',
    ],
  },
  {
    date: 'Nov 12, 2025',
    title: 'Expiring Domains — Browse 11,000+ Domains About to Expire',
    description:
      'Browse and filter thousands of expiring domains with advanced search by name, TLD, price, and expiration date.',
    accent: 'blue',
    type: 'update',
    features: [
      'Advanced filtering by domain name, TLD, price, and expiration date',
      'Sort by name, price, expiration, or creation date',
      'Accessible from the header navigation',
    ],
  },
  {
    date: 'Nov 12, 2025',
    title: 'Top Keywords — See What Others Are Searching',
    description:
      'Discover trending domain keywords. See the top 10 most searched terms from the last 30 days and search with one click.',
    accent: 'emerald',
    type: 'update',
    features: [
      'Top 10 most searched keywords from the last 30 days',
      'Search statistics and popularity metrics',
      'One-click search for any trending keyword',
    ],
  },
  {
    date: 'Nov 11, 2025',
    title: 'Enhanced Search & Multi-Provider Pricing',
    description:
      'Redesigned search with keyword vs. single-domain modes. Compare Name.com pricing side-by-side with real-time updates.',
    accent: 'blue',
    type: 'update',
    videoUrl: '/search_keyword.mov',
    features: [
      'Keyword search or single domain search',
      'Select specific TLD variants to scan',
      'Real-time pricing from multiple providers',
    ],
  },
];

const accentClasses: Record<Accent, { bg: string; light: string; badge: string; check: string }> = {
  blue:    { bg: 'bg-blue-500',    light: 'bg-blue-50',    badge: 'bg-blue-100 text-blue-600',    check: 'bg-blue-500' },
  emerald: { bg: 'bg-emerald-500', light: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-600', check: 'bg-emerald-500' },
  amber:   { bg: 'bg-amber-500',   light: 'bg-amber-50',   badge: 'bg-amber-100 text-amber-700',   check: 'bg-amber-500' },
};

const typeIcon = (type: ItemType | undefined) => {
  if (type === 'upcoming') return <Rocket className="h-5 w-5 text-white" strokeWidth={2} />;
  if (type === 'notice')   return <Clock   className="h-5 w-5 text-white" strokeWidth={2} />;
  return <Megaphone className="h-5 w-5 text-white" strokeWidth={2} />;
};

const typeLabel = (type: ItemType | undefined) => {
  if (type === 'upcoming') return 'Coming Soon';
  if (type === 'notice')   return 'Notice';
  return 'Update';
};

const AnnouncementContent: React.FC<{ item: AnnouncementItem; compact?: boolean }> = ({ item, compact }) => {
  const acc = accentClasses[item.accent];
  const isUpcoming = item.type === 'upcoming';

  return (
    <div className={`space-y-3 ${isUpcoming ? 'opacity-90' : ''}`}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-10 h-10 ${acc.bg} rounded-md flex items-center justify-center ${isUpcoming ? 'opacity-80' : ''}`}>
          {typeIcon(item.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md ${acc.badge}`}>
              {item.date}
            </span>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              {typeLabel(item.type)}
            </span>
          </div>
          <h3 className={`font-bold text-gray-900 leading-snug mb-1 ${compact ? 'text-base' : 'text-lg'}`}>
            {item.title}
          </h3>
          <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
        </div>
      </div>

      {item.videoUrl && (
        <div className="rounded-md overflow-hidden border-2 border-gray-200">
          <video src={item.videoUrl} controls className="w-full h-auto bg-black">
            Your browser does not support video playback.
          </video>
        </div>
      )}

      {item.features.length > 0 && (
        <div className={`${acc.light} rounded-md p-4 ${isUpcoming ? 'border-2 border-dashed border-current border-opacity-20' : ''}`}>
          <ul className="space-y-2">
            {item.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <div className={`flex-shrink-0 mt-0.5 w-4 h-4 ${acc.check} rounded-full flex items-center justify-center ${isUpcoming ? 'opacity-60' : ''}`}>
                  {isUpcoming
                    ? <Palette className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    : <Check    className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                  }
                </div>
                <span className="text-gray-700 text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Popup only shows the 3 most recent entries
const POPUP_LIMIT = 3;

const FeatureAnnouncement: React.FC<FeatureAnnouncementProps> = ({ variant = 'popup', onDismiss }) => {
  if (variant === 'popup') {
    const popupItems = announcements.slice(0, POPUP_LIMIT);

    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
        <div className="absolute inset-0 bg-black/40" onClick={onDismiss} />

        <div className="relative w-full max-w-xl max-h-[85vh] bg-white rounded-lg border-2 border-gray-200 flex flex-col scale-in overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-gray-100">
            <div>
              <h2 className="text-base font-bold text-gray-900 tracking-tight">What's New</h2>
              <p className="text-xs text-gray-400 font-medium">Latest updates to DomBase</p>
            </div>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6 divide-y-2 divide-gray-100">
            {popupItems.map((item, index) => (
              <div key={index} className={index > 0 ? 'pt-6' : ''}>
                <AnnouncementContent item={item} compact />
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t-2 border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
            <a href="/announcement" className="text-sm text-blue-500 hover:text-blue-600 font-medium transition-colors">
              View all {announcements.length} updates →
            </a>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="bg-blue-500 text-white font-semibold text-sm px-5 py-2 rounded-md transition-all duration-200 hover:bg-blue-600 hover:scale-105"
              >
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Page variant — full timeline
  const updates  = announcements.filter(a => a.type !== 'upcoming');
  const upcoming = announcements.filter(a => a.type === 'upcoming');

  return (
    <div className="space-y-16">
      {/* Upcoming section */}
      {upcoming.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-gray-900 rounded-md flex items-center justify-center">
              <Rocket className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Coming Soon</h2>
              <p className="text-sm text-gray-400 font-medium">What we're building next</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.map((item, index) => {
              const acc = accentClasses[item.accent];
              return (
                <div key={index} className={`${acc.light} rounded-lg p-5 border-2 border-dashed border-gray-200`}>
                  <AnnouncementContent item={item} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Changelog / shipped section */}
      <div>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Changelog</h2>
            <p className="text-sm text-gray-400 font-medium">What's already shipped</p>
          </div>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />

          <div className="space-y-10">
            {updates.map((item, index) => {
              const acc = accentClasses[item.accent];
              return (
                <div key={index} className="relative md:pl-16">
                  <div className={`absolute left-3.5 -translate-x-1/2 w-3 h-3 ${acc.bg} rounded-full hidden md:block mt-1.5`} />
                  <AnnouncementContent item={item} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureAnnouncement;
